import { ref, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useRouteQuery } from '@vueuse/router';
import { useOSettings } from '@onify/helix-core';

/**
 * Composable for managing the profile validation modal.
 *
 * Watches for the `validateContactInformation` query parameter to trigger
 * the profile modal. Dynamically reads additional query parameters that match
 * contact detail field names (e.g., `email`, `mobile_phone`) and provides
 * them as suggested values.
 *
 * @returns {Object} Reactive state and methods for the profile modal
 */
export function useProfileModal() {
  const router = useRouter();
  const route = useRoute();
  const { citizen_my_profile } = useOSettings();

  // Query parameters
  const validateContactInformation = useRouteQuery('validateContactInformation');

  // Modal visibility
  const showProfileModal = ref(false);

  const configuredContactFields = computed(() => {
    const profileSettings = citizen_my_profile?.value || {};
    const configuredValues = [profileSettings.default_contact_detail_attributes, profileSettings.required_contact_detail_attributes];
    return new Set(
      configuredValues
        .filter(Boolean)
        .flatMap((value) => value.split(','))
        .map((field) => field.trim())
        .filter(Boolean),
    );
  });

  // Watch for the query parameter to open the modal
  watch(
    [() => validateContactInformation.value, () => citizen_my_profile?.value?.enabled],
    ([value, profileEnabled]) => {
      if (value === 'true' && profileEnabled === true) {
        showProfileModal.value = true;
      } else if (value === 'true' && profileEnabled === false) {
        closeProfileModal();
      }
    },
    { immediate: true },
  );

  /**
   * Dynamically extracts suggested field values from query parameters.
   * Any query parameter whose key matches a contact detail field name
   * (e.g., email, mobile_phone) is returned as a suggested value.
   */
  const suggestedValues = computed(() => {
    const query = route.query;
    const values = {};
    for (const [key, value] of Object.entries(query)) {
      if (configuredContactFields.value.has(key) && typeof value === 'string' && value) {
        values[key] = value;
      }
    }
    return values;
  });

  // Clean up query parameters when the modal is closed
  function closeProfileModal() {
    showProfileModal.value = false;

    // Remove all profile-modal-related query params
    const query = { ...route.query };
    delete query.validateContactInformation;

    // Also remove configured field-specific params used by this flow.
    for (const key of configuredContactFields.value) {
      delete query[key];
    }

    router.replace({ query });
  }

  return {
    showProfileModal,
    suggestedValues,
    closeProfileModal,
  };
}
